import React, { useState } from "react";
import { Form, Input, Switch, Tooltip } from "antd";
import { GET_SUPPLIER_CURSOR } from "graphql/query/supplier-gql";

import { organizerUseMutation } from "../../hooks";
import { Localize, EntityRemoveButton } from "components/service";
import { localizeByKey, localizeByKeyStatic } from "components/service/localize";
import { FormElements } from "components/layout";
import type { TOrganizer } from "components/organizer/helpers/organizer-types-helper";
import { EOrganizerStatus } from "components/organizer/helpers/organizer-types-helper";
import { TSupplier } from "components/supplier/helpers/supplier-types-helper";
import Icons from "components/icons";
import { PermissionButton, useModelFormValidation, usePermissionByObject, useIsDeveloper } from "components/permissions";
import Organizers from "components/organizer";

interface IOrganizerEditFormProps
{
  organizer: TOrganizer | undefined;
}

const OrganizerEditForm: React.FC<IOrganizerEditFormProps> = ({ organizer }) =>
{
  const [ form ] = Form.useForm();
  const [ statusValue, setStatusValue ] = useState(organizer?.status === EOrganizerStatus.ACTIVE);

  const {
    _setOrganizerCreateUpdate,
    loadingMutationOrganizerCreateUpdate: loading,
  } = organizerUseMutation.createUpdate(organizer?.id);

  const { _setMutationOrganizerDelete, loadingMutationOrganizerDelete } =
    organizerUseMutation.delete(organizer?.id);

  const { validateUpdatePermission, validateDeletePermission } = useModelFormValidation(Organizers.Const);
  const { hasPermission: hasChangeStatusPermission } = usePermissionByObject(Organizers.Const.permissions.CHANGE_STATUS);
  const { hasPermission: hasUpdatePermission } = usePermissionByObject(Organizers.Const.permissions.UPDATE);
  const isDeveloper = useIsDeveloper();

  const getInitialValueBySupplierGroup = (organizer: TOrganizer | undefined, supplierGroup: string) =>
  {
    if (!organizer) {
      return undefined;
    }

    const suppliersInGroup = organizer.suppliers.filter(
      (supplier: TSupplier) => supplier.supplier_group === supplierGroup
    );

    return suppliersInGroup.length > 0
      ? suppliersInGroup.map(({ supplier_id, supplier_title }) => ({
        value: supplier_id,
        label: supplier_title,
      }))
      : undefined;
  };

  const valueFieldStatus = Form.useWatch("status", form);

  return (
    <Form
      key="edit-organizer-form"
      layout="vertical"
      form={form}
      className="model-form edit-organizer-form"
      onFinish={(values) =>
      {
        if (!validateUpdatePermission()) {
          return;
        }

        const selectedSuppliers: { supplier_group: string; supplier_id: string }[] = [];
        const groups = [ "equipment", "print_house", "flooring" ];
        const processGroup = (group: string) =>
        {
          if (values[ `suppliers-${group}` ]) {
            selectedSuppliers.push(
              ...values[ `suppliers-${group}` ].map((supplier: string) => ({
                supplier_group: group,
                supplier_id: supplier,
              }))
            );
          }
        };

        groups.forEach(processGroup);

        _setOrganizerCreateUpdate({
          variables: {
            input: {
              id: organizer?.id ? +organizer.id : undefined,
              title: values.title,
              suppliers: selectedSuppliers,
              status:
                typeof values.status === "string"
                  ? organizer?.status
                  : values?.status === true
                    ? EOrganizerStatus.ACTIVE
                    : EOrganizerStatus.CLOSED,
            },
          },
        });
      }}
    >
      <div className="switch-holder">
        <Form.Item
          name="status"
          label={<Localize>FORMS.Input_Label_Status</Localize>}
          initialValue={organizer?.status === EOrganizerStatus.ACTIVE ? true : false}
          valuePropName="checked"
          className="form-item-switch"
        >
          <Tooltip
            title={!hasChangeStatusPermission ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(Organizers.Const.permissions.CHANGE_STATUS.slug)}` : undefined}
          >
            <Switch
              disabled={!hasChangeStatusPermission}
              checked={statusValue}
              onChange={(checked) =>
              {
                setStatusValue(checked);
                form.setFieldValue('status', checked);
              }}
            />
          </Tooltip>
        </Form.Item>
        {valueFieldStatus ? (
          <span
            style={{ color: "var(--switch_colorPrimary)" }}
            className="switch-status-text"
          >
            <Localize>GLOBAL.Text_Active</Localize>
          </span>
        ) : (
          <span
            style={{ color: "var(--switch_colorTextQuaternary)" }}
            className="switch-status-text"
          >
            <Localize>GLOBAL.Text_Blocked</Localize>
          </span>
        )}
      </div>

      <Form.Item
        name="title"
        label={<Localize>FORMS.Input_Label_OrganizerName</Localize>}
        initialValue={organizer?.title}
        rules={[
          {
            required: true,
            message: <Localize>FORM_RULES.Required_OrganizerName</Localize>,
          },
          {
            max: 128,
            message: <Localize>FORM_RULES.Required_MaxCharacter</Localize>,
          },
        ]}
      >
        <Input name="title" />
      </Form.Item>

      <FormElements.CheckboxSelect
        form={form}
        query={GET_SUPPLIER_CURSOR}
        model="suppliersCursor"
        name="suppliers-equipment"
        label="FORMS.Input_Label_EquipmentSuppliers"
        modalTitle="FORMS.Title_AddEquipmentSuppliers"
        modalButtonText="MODAL.Button_Text_AddSupplier"
        initialValue={getInitialValueBySupplierGroup(organizer, "equipment")}
        skipAddButton={!hasUpdatePermission && !isDeveloper}
        disabledTags={!hasUpdatePermission && !isDeveloper ? (getInitialValueBySupplierGroup(organizer, "equipment")?.map(item => item.value) || []) : []}
      />

      <FormElements.CheckboxSelect
        form={form}
        query={GET_SUPPLIER_CURSOR}
        model="suppliersCursor"
        name="suppliers-print_house"
        label="FORMS.Input_Label_PrintHouseSuppliers"
        modalTitle="FORMS.Title_AddPrintHouseSuppliers"
        modalButtonText="MODAL.Button_Text_AddSupplier"
        initialValue={getInitialValueBySupplierGroup(organizer, "print_house")}
        skipAddButton={!hasUpdatePermission && !isDeveloper}
        disabledTags={!hasUpdatePermission && !isDeveloper ? (getInitialValueBySupplierGroup(organizer, "print_house")?.map(item => item.value) || []) : []}
      />

      <FormElements.CheckboxSelect
        form={form}
        query={GET_SUPPLIER_CURSOR}
        model="suppliersCursor"
        name="suppliers-flooring"
        label="FORMS.Input_Label_FlooringSuppliers"
        modalTitle="FORMS.Title_AddFlooringSuppliers"
        modalButtonText="MODAL.Button_Text_AddSupplier"
        initialValue={getInitialValueBySupplierGroup(organizer, "flooring")}
        skipAddButton={!hasUpdatePermission && !isDeveloper}
        disabledTags={!hasUpdatePermission && !isDeveloper ? (getInitialValueBySupplierGroup(organizer, "flooring")?.map(item => item.value) || []) : []}
      />

      <div className="form-btn-holder">
        {organizer?.id && (
          <EntityRemoveButton
            modalButton={
              <PermissionButton
                permission={Organizers.Const.permissions.DELETE}
                disabledFallback={true}
                icon={<Icons.Delete />}
              >
                <span className="ellipsis">
                  <Localize>ORGANIZER.Button_Text_Delete</Localize>
                </span>
              </PermissionButton>
            }
            nameEntity={localizeByKey("ENTITY.Modal_Title_Organizer")}
            dataNameEntity={organizer?.title}
            loading={loadingMutationOrganizerDelete}
            deleteMutation={(options) => {
              if (!validateDeletePermission()) {
                return Promise.reject(new Error('Access denied'));
              }
              return _setMutationOrganizerDelete(options);
            }}
            variables={{
              id: organizer?.id,
            }}
          />
        )}
        <PermissionButton
          permission={Organizers.Const.permissions.UPDATE}
          disabledFallback={true}
          htmlType="submit"
          className="btn-right"
          type="primary"
          icon={organizer?.id ? <Icons.Edit /> : <Icons.Plus />}
          loading={loading}
        >
          <span className="ellipsis">
            {organizer?.id ? (
              <Localize>GLOBAL.Button_Text_SaveChanges</Localize>
            ) : (
              <Localize>GLOBAL.Button_Text_CreateNew</Localize>
            )}
          </span>
        </PermissionButton>
      </div>
    </Form>
  );
};

export default OrganizerEditForm;